import React from 'react';

import { css } from '@emotion/css';
import { DropdownSingleSelect } from '@opendoor/bricks/core';

import {
  OdProtosListingsData_Listing_State as ListingState,
  OdProtosListingsData_PARNDidNotCloseReason as ParnDidNotCloseReason,
  OdProtosListingsData_PARNDidNotConvertReason as ParnDidNotConvertReason,
  OdProtosListingsData_PARN_State as ParnState,
} from '../../__generated__/athenaGlobalTypes';
import { DropdownOptionTypes } from './ParnStatusForm';

const styles = {
  text_label_bottom_padding: css`
    padding-bottom: 7px;
  `,
  field_bottom_padding: css`
    padding-bottom: 25px;
    width: 100%;
  `,
};

interface OptionType {
  label: string;
  value: ParnState | ListingState | ParnDidNotConvertReason | ParnDidNotCloseReason;
}

export const PARN_STATUS_DROPDOWN_STATES = [
  ParnState.LISTING_INITIATED,
  ParnState.FIRST_CONTACT_ATTEMPTED,
  ParnState.FIRST_CONTACT_MADE,
  ParnState.SELECTED_PARN,
  ListingState.LISTING_ACTIVE,
  ListingState.IN_CONTRACT,
  ListingState.SOLD,
  ParnState.UNKNOWN,
  ParnState.DID_NOT_CONVERT_NOT_BUYING_SELLING,
  ParnState.DID_NOT_CONVERT_COMPETITOR,
  ParnState.DID_NOT_CONVERT_NON_RESPONSIVE,
  ParnState.DID_NOT_CONVERT_NOT_INTERESTED,
  ParnState.DID_NOT_CLOSE_NO_ACCEPTABLE_OFFERS,
  ParnState.DID_NOT_CLOSE_PLANS_CHANGED,
  ParnState.DID_NOT_CLOSE_OTHER,
];

/** Lead did not convert options array */
export const PARN_LEAD_DID_NOT_CONVERT_OPTIONS: OptionType[] = [
  {
    label: 'Not buying or selling',
    value: ParnDidNotConvertReason.PARN_NOT_BUYING_OR_SELLING,
  },
  {
    label: 'Competitor',
    value: ParnDidNotConvertReason.PARN_COMPETITOR,
  },
  {
    label: 'Non-responsive',
    value: ParnDidNotConvertReason.PARN_NON_RESPONSIVE,
  },
  {
    label: 'Not interested',
    value: ParnDidNotConvertReason.NOT_INTERESTED,
  },
];

/** Listing did not close options array */
export const PARN_LISTING_DID_NOT_CLOSE_OPTIONS: OptionType[] = [
  {
    label: 'No acceptable offers',
    value: ParnDidNotCloseReason.NO_ACCEPTABLE_OFFERS,
  },
  {
    label: 'Plans changed',
    value: ParnDidNotCloseReason.PLANS_CHANGED,
  },
  {
    label: 'Other',
    value: ParnDidNotCloseReason.OTHER,
  },
];

interface ParnDropdownFieldProps {
  id: string;
  title: string;
  ariaLabel: string;
  placeholder: string;
  value: DropdownOptionTypes;
  optionHandler: (option: DropdownOptionTypes) => void;
  options: OptionType[];
  currentState: ParnState | ListingState;
}

// storybook-check-ignore
export const ParnDropdownField: React.FC<ParnDropdownFieldProps> = ({
  id,
  title,
  ariaLabel,
  placeholder,
  value,
  optionHandler,
  options,
  currentState,
}) => {
  const disableOption = (option: unknown): boolean => {
    const castOption = option as OptionType; // Cast to the expected type
    return castOption.value === currentState;
  };

  return (
    <div className={styles.field_bottom_padding}>
      <DropdownSingleSelect
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel}
        autoComplete="off"
        variant="large"
        title={title}
        placeholder={placeholder}
        selectedValue={value ? [value] : []}
        onChange={(option) => optionHandler(option as DropdownOptionTypes)}
        options={options}
        isOptionDisabled={disableOption}
        styles={{
          control: () => ({
            width: '100%',
          }),
        }}
        isRequired
      />
    </div>
  );
};

export default ParnDropdownField;
